'use client';

import {useEffect} from 'react';
import {CommonComponentContextProps, CommonComponentContext} from '@/context/ComponentContext';
import {
	useAccountCountry,
	useAppInfo,
	useCspViolation,
	useLocation,
	useSnackbarListener
} from '@/hooks';
import {isBrowser} from '@/lib/utils/page.utils';
import {EDSProvider, SnackbarProvider} from '@eds-web/react';
import {LocationProvider} from '@epic-mw/app-context';
import {useLocale} from '@epic-mw/i18n';
import {MotionConfig} from 'framer-motion';
import {RedeemStepsProvider} from '@/providers/RedeemStepsProvider';
import {SiteSettingsProvider} from '@/providers/SiteSettingsProvider';
import {getLocaleDirection} from '@/lib/utils/i18n.utils';
import {SiteSettingFieldsFragment} from '@common/hygraph-schema';
import {useEpicLocale} from '@/hooks/browser/useEpicLocale';
import {registerTimeAgoLocale} from '@/lib/utils/registerTimeAgoLocale';

interface ClientPageProvidersProps {
	children?: React.ReactNode;
	componentContext: CommonComponentContextProps;
	isLoggedIn: boolean;
	siteSettings: SiteSettingFieldsFragment;
}

/** Add in here if it has to be after providers */
const Listeners = () => {
	useSnackbarListener();
	useCspViolation();
	return <></>;
};

export function ClientProviders({
	children,
	componentContext: ctx,
	isLoggedIn,
	siteSettings
}: ClientPageProvidersProps) {
	// const { auth } = useAuth(!componentContext.enableAuth);
	const accountCountry = useAccountCountry();
	const appInfo = useAppInfo();
	const location = useLocation();
	const locale = useLocale();
	const epicLocale = useEpicLocale();
	const dir = getLocaleDirection(locale);

	useEffect(() => {
		registerTimeAgoLocale(locale);
	}, [locale]);

	if (isBrowser()) {
		window._epicAppName = appInfo.data?.name || '';
		window._epicAppVersion = appInfo.data?.version || '';
		window._epicTrackingAccountCountry = accountCountry || '';
		window._epicTrackingAuthenticated = isLoggedIn ? 'true' : 'false';
		window._epicResolvedLocale = locale;
		window._epicSetLangLocale = epicLocale;
		// window._epicTrackingAuthenticated = auth?.isLoggedIn ? "true" : "false";
	}

	return (
		<EDSProvider dir={dir} themeClass={''}>
			<LocationProvider location={location.url.toString()}>
				<CommonComponentContext.Provider value={{...ctx, isRTL: dir === 'rtl'}}>
					<MotionConfig reducedMotion="user">
						<SnackbarProvider position="topRight" duration={8000}>
							<Listeners />
							<SiteSettingsProvider siteSettings={siteSettings}>
								<RedeemStepsProvider>{children}</RedeemStepsProvider>
							</SiteSettingsProvider>
						</SnackbarProvider>
					</MotionConfig>
				</CommonComponentContext.Provider>
			</LocationProvider>
		</EDSProvider>
	);
}
